import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuCheck, LuLoader, LuUsers, LuX } from 'react-icons/lu';
import { toast } from 'react-toast';
import axiosInstance from '../../api/axiosInstance';
import { formatDate } from '../../utils/dateTimeFormat';
import { RtModal } from '../ui/RtModal';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getInitials = (name = '', email = '') => {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const todayLabel = () => formatDate(new Date());

const ShareReportModal = ({ isOpen, onClose, reportContext }) => {
  const projectId = reportContext?.projectId;
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState(() => new Set());
  const [externalEmails, setExternalEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shareResult, setShareResult] = useState(null);

  const resetState = useCallback(() => {
    setMembers([]);
    setMembersError(null);
    setSelectedEmails(new Set());
    setExternalEmails([]);
    setEmailInput('');
    setSubmitting(false);
    setShareResult(null);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetState();
    onClose?.();
  }, [submitting, resetState, onClose]);

  // Fetch project members whenever the modal opens for a project
  useEffect(() => {
    if (!isOpen || !projectId || projectId === 'all') return undefined;

    let cancelled = false;
    const fetchMembers = async () => {
      setMembersLoading(true);
      setMembersError(null);
      try {
        const response = await axiosInstance.get(
          `/projects/list_project_members?project_id=${projectId}`,
          { skipGlobalToast: true, skipGlobalLoader: true }
        );
        if (cancelled) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setMembers(list);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching project members:', err);
        setMembers([]);
        setMembersError('Failed to load project members.');
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    };

    fetchMembers();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  const memberEmails = useMemo(
    () =>
      members
        .map((member) => String(member?.email || '').trim().toLowerCase())
        .filter(Boolean),
    [members]
  );

  const allSelected =
    memberEmails.length > 0 && memberEmails.every((email) => selectedEmails.has(email));

  const toggleMember = (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) return;
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(normalized)) next.delete(normalized);
      else next.add(normalized);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedEmails((prev) => {
      if (memberEmails.every((email) => prev.has(email))) {
        const next = new Set(prev);
        memberEmails.forEach((email) => next.delete(email));
        return next;
      }
      return new Set([...prev, ...memberEmails]);
    });
  };

  const commitEmailInput = (rawValue) => {
    const value = String(rawValue ?? emailInput).trim().replace(/[,;]$/, '').trim();
    if (!value) {
      setEmailInput('');
      return;
    }
    const normalized = value.toLowerCase();
    if (!EMAIL_REGEX.test(normalized)) {
      toast.error(`"${value}" is not a valid email address.`);
      return;
    }
    if (memberEmails.includes(normalized)) {
      setSelectedEmails((prev) => new Set(prev).add(normalized));
      setEmailInput('');
      return;
    }
    setExternalEmails((prev) =>
      prev.includes(normalized) ? prev : [...prev, normalized]
    );
    setEmailInput('');
  };

  const handleEmailKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      commitEmailInput();
    } else if (event.key === 'Backspace' && !emailInput && externalEmails.length) {
      setExternalEmails((prev) => prev.slice(0, -1));
    }
  };

  const removeExternalEmail = (email) => {
    setExternalEmails((prev) => prev.filter((item) => item !== email));
  };

  const recipientEmails = useMemo(() => {
    const set = new Set([...selectedEmails, ...externalEmails]);
    return Array.from(set);
  }, [selectedEmails, externalEmails]);

  const handleShare = async () => {
    if (!reportContext?.payload) {
      toast.error('Report details are unavailable. Please try again.');
      return;
    }
    const pendingInput = emailInput.trim();
    let emails = recipientEmails;
    if (pendingInput) {
      const normalized = pendingInput.toLowerCase();
      if (!EMAIL_REGEX.test(normalized)) {
        toast.error(`"${pendingInput}" is not a valid email address.`);
        return;
      }
      emails = Array.from(new Set([...emails, normalized]));
    }

    if (emails.length === 0) {
      toast.error('Select at least one recipient to share the report.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axiosInstance.post(
        '/report/share_report',
        { ...reportContext.payload, emails },
        { skipGlobalToast: true }
      );
      const statusCode = response?.status_code ?? response?.status;
      if (statusCode && statusCode >= 400) {
        throw new Error(response?.message || 'Failed to share report.');
      }
      setShareResult({
        message: response?.message || 'Report shared successfully.',
        emails: response?.data?.emails?.length ? response.data.emails : emails,
        projectName: response?.data?.project_name || reportContext.projectName,
        dateRange: response?.data?.date_range || null,
      });
      toast.success( "Email sent successfully! You will receive it within 5 minutes."||response?.message || 'Report shared successfully.');
      setEmailInput('');
      setTimeout(() => {
        resetState();
        onClose?.();
      }, 1800);
    } catch (err) {
      console.error('Error sharing report:', err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to share report. Please try again.';
      toast.error(typeof message === 'string' ? message : 'Failed to share report.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const recipientCount = recipientEmails.length + (emailInput.trim() ? 1 : 0);
  const subtitle = [reportContext?.reportId, reportContext?.projectName, todayLabel()]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <RtModal
      open={isOpen}
      onClose={handleClose}
      title="Share report"
      subtitle={subtitle}
      size="md"
      closeDisabled={submitting}
      closeOnBackdrop={!submitting}
      footer={
        shareResult ? null : (
          <>
            <button
              type="button"
              className="rt-btn rt-btn--secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rt-btn rt-btn--primary"
              onClick={handleShare}
              disabled={submitting || recipientCount === 0}
            >
              {submitting && <LuLoader size={14} className="animate-spin" />}
              {submitting ? 'Sharing…' : 'Share report'}
            </button>
          </>
        )
      }
    >
      {shareResult ? (
        <div className="rt-modal-confirm" style={{ textAlign: 'center' }}>
          <span
            className="rt-modal-confirm__icon"
            style={{
              display: 'inline-flex',
              width: 48,
              height: 48,
              borderRadius: '50%',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f0fdf4',
              color: '#16a34a',
              marginBottom: 8,
            }}
            aria-hidden
          >
            <LuCheck size={24} strokeWidth={2.5} />
          </span>
          <p className="rt-modal-confirm__message">{shareResult.message}</p>
          <p className="rt-modal__hint">
            Shared with {shareResult.emails.length}{' '}
            {shareResult.emails.length === 1 ? 'recipient' : 'recipients'}
            {shareResult.dateRange ? `  ·  ${shareResult.dateRange}` : ''}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Project members
              </span>
              {memberEmails.length > 0 && (
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-xs font-medium text-blue-600 transition hover:text-blue-700"
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>

            <div className="max-h-[204px] overflow-y-auto rounded-xl border border-slate-200">
              {membersLoading ? (
                <div className="flex items-center justify-center gap-2 px-3 py-8 text-xs text-slate-400">
                  <LuLoader size={14} className="animate-spin" />
                  Loading members…
                </div>
              ) : membersError ? (
                <div className="px-3 py-8 text-center text-xs text-red-500">
                  {membersError}
                </div>
              ) : members.length === 0 ? (
                <div className="flex flex-col items-center gap-1.5 px-3 py-8 text-center text-xs text-slate-400">
                  <LuUsers size={18} />
                  No members found for this project.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {members.map((member) => {
                    const email = String(member?.email || '').trim().toLowerCase();
                    const checked = selectedEmails.has(email);
                    return (
                      <li key={member?.user_id ?? email}>
                        <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleMember(email)}
                            className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500"
                          />
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[11px] font-semibold text-blue-600">
                            {getInitials(member?.name, member?.email)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-700">
                              {member?.name || member?.email || 'Unknown member'}
                            </span>
                            <span className="block truncate text-xs text-slate-400">
                              {member?.email}
                            </span>
                          </span>
                          {member?.role && (
                            <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium capitalize text-slate-500">
                              {member.role}
                            </span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div>
            <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Add others by email
            </span>
            <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-2 transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
              {externalEmails.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeExternalEmail(email)}
                    className="text-blue-400 transition hover:text-blue-600"
                    aria-label={`Remove ${email}`}
                  >
                    <LuX size={12} />
                  </button>
                </span>
              ))}
              <input
                type="email"
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                onKeyDown={handleEmailKeyDown}
                onBlur={() => commitEmailInput()}
                placeholder={externalEmails.length ? '' : 'name@company.com'}
                className="min-w-[140px] flex-1 border-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
              />
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              Press enter to add. External recipients get view-only access.
            </p>
          </div>
        </div>
      )}
    </RtModal>
  );
};

export default ShareReportModal;
